import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  ISelectOption,
  MarketCategoryFilterType,
  MarketSortByType,
} from "../../../common/commonTypes";
import { getParagraph14BoldTypography } from "../../../common/Typography";
import FilterSort from "./FilterSort";
import { getHorizontalGap } from "../../../common/styles";
import { useMarketFilters } from "../../../queries/market.query";

const Root = styled.div`
  padding-bottom: 48px;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    padding-bottom: 28px;
  }
`;

const CategoryContainer = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: row;

  ${getHorizontalGap("36px")}
  padding-bottom: 20px;
  max-width: 350px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    justify-content: space-between;
    ${getHorizontalGap("0px")}
  }
`;

const CategoryChip = styled.button<any>`
  ${getParagraph14BoldTypography};
  padding: 6px 12px;
  background: ${(props) =>
    props.isActive ? "rgba(255, 255, 255, 0.1)" : "transparent"};
  border-radius: ${(props) => (props.isActive ? "6px" : "0px")};
  color: ${(props) =>
    props.isActive ? props.theme.primaryColor : "rgba(255, 255, 255, 0.5)"};
  cursor: pointer;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    ${getParagraph14BoldTypography};
    line-height: 16px;
  }
`;

const Divider = styled.hr`
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

type Props = {
  category: MarketCategoryFilterType;
  lang?: string;
  genre?: string;
  sortBy: MarketSortByType;
  isForSale: boolean;
  onToggleForSale: (toggle: boolean) => void;
  onChangeCategory: (category: MarketCategoryFilterType) => void;
  onSelectLang: (option: ISelectOption) => void;
  onSelectGenre: (option: ISelectOption) => void;
  onSelectSortBy: (option: ISelectOption<MarketSortByType>) => void;
  onResetFilters: () => void;
};

const MarketFilters = ({
  category,
  lang,
  genre,
  sortBy,
  isForSale,
  onToggleForSale,
  onChangeCategory,
  onSelectLang,
  onSelectGenre,
  onSelectSortBy,
  onResetFilters,
}: Props) => {
  const { t } = useTranslation();
  const { filters } = useMarketFilters();
  const { languages, genres } = filters;

  return (
    <Root>
      <CategoryContainer>
        <CategoryChip
          isActive={category === "all"}
          onClick={() => onChangeCategory("all")}
        >
          {t("market-category-all-tracks")}
        </CategoryChip>
        <CategoryChip
          isActive={category === "hot"}
          onClick={() => onChangeCategory("hot")}
        >
          {t("market-category-whats-hot")}
        </CategoryChip>
        <CategoryChip
          isActive={category === "drops"}
          onClick={() => onChangeCategory("drops")}
        >
          {t("market-category-new-drops")}
        </CategoryChip>
      </CategoryContainer>

      <Divider />

      <FilterSort
        lang={lang}
        genre={genre}
        sortBy={sortBy}
        languages={languages}
        genres={genres}
        isForSale={isForSale}
        onToggleForSale={onToggleForSale}
        onSelectLang={onSelectLang}
        onSelectGenre={onSelectGenre}
        onSelectSortBy={onSelectSortBy}
        onResetFilters={onResetFilters}
      />
    </Root>
  );
};

export default MarketFilters;
